import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { vendorShops, vendorGetShop, vendorUpdateStatus } from "@/lib/vendorApi";
import { fetchPublicActivityProfile, formatApiError } from "@/lib/api";
import client from "@/lib/api";
import { publishShopStatus } from "@/lib/liveShopStatus";
import { useApp } from "@/context/AppContext";
import AvailabilityToggle from "@/components/AvailabilityToggle";
import ShareSheet from "@/components/ShareSheet";

// Vendor-only: own Active / Pending ad counts for the menu (from /vendor/ads/summary, owner-scoped on the server).
export function AdsMenuCounts() {
  const { isVendor, user } = useApp();
  const [c, setC] = useState(null);
  useEffect(() => {
    setC(null);
    if (isVendor) client.get("/vendor/ads/summary").then((r) => setC(r.data)).catch(() => {});
  }, [isVendor, user?.id]);
  if (!c) return null;
  return (
    <span className="ms-auto flex items-center gap-1 text-[10px] font-semibold" data-testid="ads-menu-counts">
      <span className="whitespace-nowrap rounded-full bg-emerald-100 text-emerald-800 px-1.5 py-0.5" data-testid="ads-menu-active">{c.active} active</span>
      <span className="whitespace-nowrap rounded-full bg-amber-100 text-amber-800 px-1.5 py-0.5" data-testid="ads-menu-pending">{c.pending} pending</span>
    </span>
  );
}

// Vendor-only: same Shop Open/Closed control as the dashboard ShopStatusCard (one toggle per owned shop).
export function MenuShopAvailability() {
  const { isVendor } = useApp();
  const [shops, setShops] = useState([]);
  const [busy, setBusy] = useState("");
  const load = () => vendorShops().then((list) => Promise.all((list || []).map((s) => vendorGetShop(s.shop_id || s.id).catch(() => null)))).then((full) => setShops(full.filter(Boolean))).catch(() => {});
  useEffect(() => { if (isVendor) load(); }, [isVendor]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!isVendor || !shops.length) return null;
  const toggle = async (shop) => {
    const open = !!shop.status?.is_open;
    const next = open ? "manual_closed" : "manual_open";
    setBusy(shop.shop_id);
    try {
      await vendorUpdateStatus(shop.shop_id, next);
      publishShopStatus(shop.shop_id, next === "manual_open" ? { is_open: true, label: "Open", reason: "manual_open" } : { is_open: false, label: "Closed by shop", reason: "manual" });
      toast.success(next === "manual_open" ? "Shop is now OPEN" : "Shop is now CLOSED");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(""); }
  };
  return (
    <div className="space-y-2" data-testid="menu-shop-availability">
      {shops.map((s) => (
        <AvailabilityToggle key={s.shop_id} active={!!s.status?.is_open} onToggle={() => toggle(s)} busy={busy === s.shop_id}
          eyebrow={shops.length > 1 ? s.name : "Shop Availability"} activeTitle="Available" inactiveTitle="Not Available"
          activeHint="Shop Open" inactiveHint="Shop Closed" ariaLabel={`Shop availability for ${s.name}`} testid={`menu-shop-availability-${s.shop_id}`} />
      ))}
    </div>
  );
}

// Opens the existing QR share card for the signed-in user's public shop (vendor) or published public profile.
export function ShareMyProfileSheet({ open, onClose }) {
  const { user, isVendor } = useApp();
  const [target, setTarget] = useState(null);
  useEffect(() => {
    if (!open || !user) { setTarget(null); return; }
    const origin = window.location.origin;
    const viaProfile = () => fetchPublicActivityProfile(user.id).then((p) => {
      if (!p?.sections || !Object.keys(p.sections).length) throw new Error("none");
      setTarget({ name: p.name || user.name, tagline: "Public profile", url: `${origin}/p/${user.id}`, noun: "profile" });
    });
    const pick = isVendor
      ? vendorShops().then((list) => {
        const s = (list || []).find((x) => x.listed !== false) || (list || [])[0];
        if (!s) return viaProfile();
        setTarget({ name: s.name, tagline: s.tagline || s.description, url: `${origin}/v/${s.slug || s.shop_id}`, noun: "shop", shop: s });
        return null;
      })
      : viaProfile();
    pick.catch(() => { toast.info("Aapka public profile abhi publish nahi hai — Profile page par activity profile publish karein."); onClose(); });
  }, [open, user?.id, isVendor]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!open || !target) return null;
  // Portal: the sticky, blurred header would otherwise become the containing block of the fixed overlay.
  return createPortal(
    <ShareSheet open onClose={onClose} shopName={target.name} tagline={target.tagline} shareUrl={target.url} profileUrl={target.url}
      shop={target.shop} noun={target.noun} heading={target.noun === "shop" ? "Share my shop" : "Share my profile"} />,
    document.body,
  );
}
